import React, { useEffect, useState } from "react";
import { doc, getDoc, collection, query, where, getDocs, updateDoc, increment, addDoc } from "firebase/firestore";
import { db } from "../firebase";
import Toast from "./Toast";

// Klanten-tab van het winkeldashboard: klantenlijst, zoeken/koppelen van
// nieuwe klanten en stempels toevoegen/inwisselen. Losgetrokken uit het
// oorspronkelijke (1000+ regels) ShopDashboard.js zodat elke tab zijn eigen,
// overzichtelijke bestand heeft — zelfde aanpak als ShopInfo/ShopInsights.
const ShopCustomers = ({ shopId, onCountChange }) => {
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customersIndexBuilding, setCustomersIndexBuilding] = useState(false);
  const [customersIndexUrl, setCustomersIndexUrl] = useState("");
  const [customersRetryIn, setCustomersRetryIn] = useState(0);
  const [customersRetryAttempts, setCustomersRetryAttempts] = useState(0);
  const [customersReloadTick, setCustomersReloadTick] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [stampsRequired, setStampsRequired] = useState(10);
  const [toast, setToast] = useState(null);

  // Zoeken naar nieuwe klanten
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const showToast = (message, type = "success") => setToast({ message, type });

  useEffect(() => {
    if (!shopId) return;
    // Alleen stampsRequired nodig voor het "klaar voor beloning"-filter
    // hieronder; de rest van de winkelinstellingen leeft in ShopSettings.
    getDoc(doc(db, "shops", shopId)).then((snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setStampsRequired(parseInt(data.stampsRequired) || 10);
      }
    });
    loadCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId, customersReloadTick]);

  const loadCustomers = async () => {
    if (!shopId) return;
    setCustomersLoading(true);
    try {
      const shopCustomersRef = collection(db, "shopCustomers");
      const q = query(shopCustomersRef, where("shopId", "==", shopId));
      const querySnapshot = await getDocs(q);

      const customersData = [];
      for (const docSnap of querySnapshot.docs) {
        const data = docSnap.data();
        customersData.push({
          id: data.userId,
          email: data.userEmail || "Onbekend",
          name: data.userName || "Onbekend",
          stamps: data.stamps || 0,
          lastVisit: data.lastVisit || null,
          totalVisits: data.totalVisits || 0,
          rewardsRedeemed: data.rewardsRedeemed || 0,
          isAdmin: data.isAdmin || false
        });
      }
      setCustomers(customersData);
      if (onCountChange) onCountChange(customersData.length);
      setCustomersIndexBuilding(false);
      setCustomersIndexUrl("");
      setCustomersRetryAttempts(0);
    } catch (error) {
      console.error('Error loading customers:', error);
      const msg = error?.message || "";
      const isIndexIssue = error?.code === 'failed-precondition' || msg.includes('requires an index') || msg.includes('index is currently building');
      if (isIndexIssue) {
        const m = msg.match(/https?:\/\/\S+/);
        setCustomersIndexUrl(m ? m[0] : "");
        setCustomersIndexBuilding(true);
        const attempts = customersRetryAttempts + 1;
        setCustomersRetryAttempts(attempts);
        const waitSec = Math.min(30, 5 * attempts);
        setCustomersRetryIn(waitSec);
        let remaining = waitSec;
        const timer = setInterval(() => {
          remaining -= 1;
          setCustomersRetryIn(prev => (prev > 0 ? prev - 1 : 0));
          if (remaining <= 0) clearInterval(timer);
        }, 1000);
        if (attempts <= 6) {
          setTimeout(() => setCustomersReloadTick(t => t + 1), waitSec * 1000);
        }
      } else {
        showToast("Fout bij laden klanten: " + error.message, "error");
      }
      setCustomers([]);
      if (onCountChange) onCountChange(0);
    }
    setCustomersLoading(false);
  };

  const addStamp = async (customerId) => {
    try {
      const shopCustomersRef = collection(db, "shopCustomers");
      const q = query(shopCustomersRef, where("shopId", "==", shopId), where("userId", "==", customerId));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        showToast("Klant niet gevonden. Probeer de lijst te verversen.", "error");
        return;
      }

      const docRef = doc(db, "shopCustomers", querySnapshot.docs[0].id);
      await updateDoc(docRef, {
        stamps: increment(1),
        lastVisit: new Date(),
        totalVisits: increment(1)
      });
      showToast("Stempel toegevoegd!");
      loadCustomers();
    } catch (error) {
      showToast("Fout bij toevoegen stempel: " + error.message, "error");
    }
  };

  const redeemReward = async (customerId) => {
    try {
      const shopCustomersRef = collection(db, "shopCustomers");
      const q = query(shopCustomersRef, where("shopId", "==", shopId), where("userId", "==", customerId));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        showToast("Klant niet gevonden. Probeer de lijst te verversen.", "error");
        return;
      }

      const docRef = doc(db, "shopCustomers", querySnapshot.docs[0].id);
      await updateDoc(docRef, {
        stamps: 0,
        rewardsRedeemed: increment(1)
      });
      showToast("Beloning ingewisseld!");
      loadCustomers();
    } catch (error) {
      showToast("Fout bij inwisselen beloning: " + error.message, "error");
    }
  };

  const searchUsers = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const usersRef = collection(db, "users");
      const searchLower = searchQuery.toLowerCase();

      const q = query(usersRef);
      const querySnapshot = await getDocs(q);

      const results = [];
      querySnapshot.forEach((docSnap) => {
        const userData = docSnap.data();
        const fullName = `${userData.firstName || ""} ${userData.lastName || ""}`.toLowerCase();
        const email = (userData.email || "").toLowerCase();

        if (fullName.includes(searchLower) || email.includes(searchLower)) {
          results.push({
            id: docSnap.id,
            ...userData
          });
        }
      });

      setSearchResults(results);
    } catch (error) {
      showToast("Fout bij zoeken: " + error.message, "error");
      setSearchResults([]);
    }
    setSearching(false);
  };

  const giveStampCard = async (userId, userEmail, userName) => {
    try {
      const shopCustomersRef = collection(db, "shopCustomers");
      const q = query(shopCustomersRef, where("shopId", "==", shopId), where("userId", "==", userId));
      const existingCustomer = await getDocs(q);

      if (!existingCustomer.empty) {
        showToast("Deze gebruiker heeft al een stempelkaart bij deze winkel.", "error");
        return;
      }

      await addDoc(shopCustomersRef, {
        shopId: shopId,
        userId: userId,
        userEmail: userEmail,
        userName: userName,
        stamps: 0,
        totalVisits: 0,
        rewardsRedeemed: 0,
        isAdmin: false,
        createdAt: new Date()
      });

      showToast(`Stempelkaart gegeven aan ${userName}!`);
      setSearchResults([]);
      setSearchQuery("");

      setTimeout(() => {
        loadCustomers();
      }, 500);
    } catch (error) {
      showToast("Fout bij geven stempelkaart: " + error.message, "error");
    }
  };

  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         customer.email.toLowerCase().includes(searchTerm.toLowerCase());

    if (filterStatus === "all") return matchesSearch;
    if (filterStatus === "admin") return matchesSearch && customer.isAdmin;
    if (filterStatus === "regular") return matchesSearch && !customer.isAdmin;
    if (filterStatus === "ready") return matchesSearch && customer.stamps >= stampsRequired;

    return matchesSearch;
  });

  return (
    <div className="card card-flat">
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3>Klanten</h3>
        <button onClick={loadCustomers} className="btn btn-primary" disabled={customersLoading}>
          {customersLoading ? 'Laden...' : 'Verversen'}
        </button>
      </div>
      {customersIndexBuilding && (
        <div style={{ padding: '10px 16px', background: '#fff3cd', color: '#856404', borderTop: '1px solid #ffeeba' }}>
          <div style={{ marginBottom: 4 }}>Index aan het bouwen voor klanten.</div>
          {customersIndexUrl ? (
            <a href={customersIndexUrl} target="_blank" rel="noreferrer">Open index status</a>
          ) : (
            <span>Index aan het bouwen...</span>
          )}
          <span style={{ marginLeft: 6 }}>
            {customersRetryAttempts <= 6 ? `Automatische poging over ${customersRetryIn}s.` : 'Automatische pogingen gestopt.'}
          </span>
          <button onClick={loadCustomers} className="btn btn-secondary" style={{ marginLeft: 10, padding: '2px 8px' }}>Opnieuw proberen</button>
        </div>
      )}
      <div className="card-body">
        {/* Zoeken naar nieuwe klanten */}
        <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
          <h4 style={{ marginBottom: '10px' }}>Zoek en voeg klanten toe</h4>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
            <input
              type="text"
              placeholder="Zoek op naam of email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="form-input"
              style={{ flex: 1 }}
            />
            <button onClick={searchUsers} className="btn btn-secondary" disabled={searching}>
              {searching ? 'Zoeken...' : 'Zoeken'}
            </button>
          </div>

          {searchResults.length > 0 && (
            <div style={{ marginTop: '10px' }}>
              <h5>Gevonden gebruikers:</h5>
              <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {searchResults.map((user) => (
                  <div key={user.id} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    marginBottom: '5px',
                    backgroundColor: 'white'
                  }}>
                    <div>
                      <strong>{user.firstName} {user.lastName}</strong>
                      <div style={{ fontSize: '12px', color: '#666' }}>{user.email}</div>
                    </div>
                    <button
                      onClick={() => giveStampCard(user.id, user.email, `${user.firstName} ${user.lastName}`)}
                      className="btn btn-success"
                      style={{ fontSize: '12px' }}
                    >
                      Stempelkaart geven
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Filter bestaande klanten */}
        <div style={{ marginBottom: '20px' }}>
          <input
            type="text"
            placeholder="Filter klanten..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="form-input"
            style={{ width: '200px', marginRight: '10px' }}
          />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="form-input"
            style={{ width: '150px' }}
          >
            <option value="all">Alle klanten</option>
            <option value="admin">Alleen admins</option>
            <option value="regular">Alleen klanten</option>
            <option value="ready">Klaar voor beloning</option>
          </select>
        </div>

        <table>
          <thead>
            <tr>
              <th>Naam</th>
              <th>Email</th>
              <th>Stempels</th>
              <th>Laatste bezoek</th>
              <th>Acties</th>
            </tr>
          </thead>
          <tbody>
            {filteredCustomers.map((customer) => (
              <tr key={customer.id}>
                <td>
                  {customer.name}
                  {customer.isAdmin && <span style={{ color: 'blue', marginLeft: '5px' }}>(Admin)</span>}
                </td>
                <td>{customer.email}</td>
                <td>{customer.stamps}</td>
                <td>
                  {customer.lastVisit ? customer.lastVisit.toDate().toLocaleDateString() : 'Nooit'}
                </td>
                <td>
                  <button
                    onClick={() => addStamp(customer.id)}
                    className="btn btn-primary"
                    style={{ marginRight: '5px' }}
                  >
                    +1
                  </button>
                  {customer.stamps >= stampsRequired && (
                    <button
                      onClick={() => redeemReward(customer.id)}
                      className="btn btn-success"
                    >
                      Inwisselen
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
};

export default ShopCustomers;
